<template>
  <cms-base-expandable-cta
    v-if="ctaType === 'multilink'"
    :size="expandableSize"
    :targets
    :text="multilinkTitle"
    :variant="multilinkStyle"
    @click="$emit('click', $event, targets[$event])"
  />
  <div v-else :class="[layout, equalSizeClass]" :style="{ '--cols': cols }">
    <cms-shared-button
      v-for="(target, i) in targets"
      :key="i"
      :class="buttonClass"
      v-bind="{ ...target, ...textLinkStyle }"
      :size
      @click="$emit('click', i, target)"
    />
  </div>
</template>

<script lang="ts" setup>
import type { CSSClass, Responsive } from '#types/common'
import type { ButtonSize, ButtonVariant } from '#types/components/vf/button'
import type { CtaType } from '#types/content'
import type { TextLinkStyle } from '#types/styles'
import type { Target } from '#types/target'

const {
  cols: colsOverride,
  equalSizeSmallClass = '<md:grid <md:cols-1 <md:auto-rows-fr',
  equalTargetSize,
  layout = 'pointer-within f-col gap-4 md:flex-row md:wrap',
  size,
  targets
} = defineProps<{
  buttonClass?: CSSClass
  cols?: string
  ctaType?: CtaType
  equalSizeSmallClass?: string
  equalTargetSize?: Responsive<boolean>
  layout?: CSSClass
  multilinkStyle?: Responsive<ButtonVariant>
  multilinkTitle?: string
  size?: Responsive<{ size?: ButtonSize, fullWidth?: boolean }>
  targets: Target[]
  textLinkStyle?: TextLinkStyle
}>()

defineEmits<{
  click: [index: number, target: Target]
}>()

/**
 * `text-link` targets size to their content, everything else shares the row equally.
 * With no text-links this is equivalent to `repeat(n,minmax(0,1fr))`.
 */
const cols = computed(() => colsOverride
  ?? targets.map(({ style }) => style?.sm === 'text-link' ? 'max-content' : 'minmax(0,1fr)').join(' '))

const equalSizeClass = computed(() => ({
  [equalSizeSmallClass]: getValueForBreakpoint('sm', equalTargetSize),
  '~md:grid ~md:cols-$cols ~md:auto-rows-fr ~md:items-stretch': getValueForBreakpoint('md', equalTargetSize),
  'lg:grid lg:cols-$cols lg:auto-rows-fr lg:items-stretch': getValueForBreakpoint('lg', equalTargetSize)
}))

// The expandable trigger is a single button, so it takes the plain size rather than the CTA sizing object.
const expandableSize = computed(() => size && {
  sm: size.sm?.size,
  md: size.md?.size,
  lg: size.lg?.size
})
</script>
